import { Component, OnInit, OnDestroy, QueryList, ViewChildren, ViewChild, AfterViewInit, TemplateRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, ReplaySubject, Subscription, combineLatest, of, map, startWith, switchMap, tap, debounceTime, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { TranslateService } from '@ngx-translate/core';
import { ENTER, COMMA } from '@angular/cdk/keycodes';

import { AppState } from 'src/app/core/app.state';
import { DATE_FORMAT, DATE_TIME_FORMAT, DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount } from 'src/app/shared/models/page.model';
import { SetCurrentElementId, SetCurrentElementObject } from 'src/app/shared/store/shared.actions';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { formatResponseTime, getOrderPaymentStatus } from 'src/app/shared/utils/helpers';
import { FilesDialogPreviewerComponent } from 'src/app/shared/files-dialog-previewer/files-dialog-previewer.component';

import {
  CreateLocationPayload, LocationUpdatePayload, IAvailableCatalogRow, ICurrency, ICustomer, IFavoriteBrandRow, IFavoriteCategoryRow, IFavoriteProductRow, IFile, ILocation,
  IMainContact, IPayment_term, LocationContact, SelectedCatalogsData, IResolvedProduct
} from 'src/app/features/customers/models/customers.models';
import {
  AddLocation, AddLocationSuccess,
  CustomerApprobation, CustomerApprobationSuccess,
  DeleteCustomer, DeleteCustomerSuccess,
  RestoreCustomer, RestoreCustomerSuccess,
  DeleteLocation, DeleteLocationSuccess,
  DeleteLocationContact,
  FetchAllCurrencies, FetchAllpaymentTerms,
  GetCustomerById, GetCustomerByIdError, GetCustomerByIdSuccess,
  UpdateCustomerError, UpdateCustomerSuccess,
  UpdateLocation, UpdateLocationSuccess, UpdateLocationError
} from 'src/app/features/customers/store/customers.actions';
import { AddContactsComponent } from 'src/app/features/customers/components/add-contacts/add-contacts.component';
import { AddLocationsComponent, AddLocationDialogData } from 'src/app/features/customers/components/add-locations/add-locations.component';
import { EditFilesComponent } from 'src/app/features/customers/components/edit-files/edit-files.component';
import { CustomerCustomFieldsComponent } from 'src/app/features/customers/components/customer-custom-fields/customer-custom-fields.component';

import {
  LoadCustomerEmails,
  CreateCustomerEmail,
  UpdateCustomerEmail,
  DeleteCustomerEmail,
} from 'src/app/features/knowledge-base/store/customer-emails.actions';
import {
  selectCustomerEmailsErp,
  selectCustomerEmailsVolta,
  selectCustomerEmailsLoadingFor,
  selectCustomerEmailsLastMutationError,
} from 'src/app/features/knowledge-base/store/customer-emails.selectors';
import { ICompanyEmail, IStructuredApiError } from 'src/app/features/knowledge-base/models/customer-email.models';

import {
  LoadCompanyLocationEmails,
  CreateLocationEmail,
  UpdateLocationEmail,
  DeleteLocationEmail,
} from 'src/app/features/knowledge-base/store/location-emails.actions';
import {
  selectLocationEmailsForCompanyErp,
  selectLocationEmailsForCompanyVolta,
  selectLocationEmailsLoadingForCompany,
  selectLocationEmailsLastMutationError,
} from 'src/app/features/knowledge-base/store/location-emails.selectors';
import { ILocationEmail } from 'src/app/features/knowledge-base/models/location-email.models';

import { ITag } from 'src/app/features/tags/models/tags.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ICatalog } from 'src/app/features/catalogs/models/catalogs.models';
import { ICompaniesGroups } from 'src/app/features/companies-groups/models/companies-groups.model';

import { FetchAllBrands } from 'src/app/features/brands/store/brands.actions';
import { FetchAllTags } from 'src/app/features/tags/store/tags.actions';
import { FetchAllCategories } from 'src/app/features/categories/store/categories.actions';
import { AddCompaniesGroupeSuccess, FetchCompaniesGroups } from 'src/app/features/companies-groups/store/companies-groups.actions';

import { IPriceBookSimple, PriceBookTypeValue } from 'src/app/features/pricebook/models/pricebook.model';
import { PricebookService } from 'src/app/features/pricebook/services/pricebook.service';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import { IObjectCustomFields, IObjectCustomFieldsParams } from 'src/app/features/settings/models/settings.models';
import { CustomFieldVisibilityTypes, RelatedObjectsTypes } from 'src/app/features/settings/models/custom-fields.constants';
import { GetObjectCustomFields, UpdateObjectCustomFields } from 'src/app/features/settings/store/settings.actions';
import { LanguageService } from 'src/app/config/language.service';
import { AddElementsComponent } from 'src/app/features/produits/components/add-elements/add-elements.component';

import { RecapHelpersService } from './recap-helpers.service';
import {
  AvailableCatalogRow,
  AvailableCatalogSortColumn,
  ProductFavoriteBrandRow,
  ProductFavoriteCategoryRow,
  ProductFavoriteProductRow,
  ProductFavoriteSortColumn,
} from './recap-location.types';


/** Customer detail view for a location; tabs load from a single GetCustomerById response. */
@Component({
  selector: 'app-recap-location',
  templateUrl: './recap-location.component.html',
  styleUrls: ['./recap-location.component.scss']
})
export class RecapLocationComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChildren(MatAutocompleteTrigger) autocompleteTriggers!: QueryList<MatAutocompleteTrigger>;
  @ViewChild('resolvedProductsDialogTpl', { static: false }) resolvedProductsDialogTpl!: TemplateRef<any>;

  activeResolvedType: 'whitelist' | 'blacklist' | null = null;
  rpSearchQuery = '';

  page: IPageCount = { size: DEFAULT_PAGE_SIZE, number: 1 };
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  form!: FormGroup;

  locationId!: number;
  customer!: ICustomer;
  currentLocation!: ILocation;
  otherLocations: ILocation[] = [];
  isLoaded = false;

  contacts_information: IMainContact[] = [];
  selectedCatalogs: ICatalog[] = [];
  filteredCatalogs: ICatalog[] = [];
  availableCatalogs: AvailableCatalogRow[] = [];
  filteredAvailableCatalogs: AvailableCatalogRow[] = [];
  catalogSearchInput = new FormControl('');
  blacklist_brands: IBrand[] = [];
  favorit_brands: IBrand[] = [];
  categories: ICategory[] = [];
  tags: ITag[] = [];
  groupes: ICompaniesGroups[] = [];
  allAvailableGroupes: ICompaniesGroups[] = [];
  selectedFiles: IFile[] = [];
  currentCustomDiscount!: number;
  custom_discount = new FormControl(0);

  currentForm: string = 'recapCatalog';
  activeTab: string = 'orders';
  rightPanelTab: string = 'details';

  // Customers we've already triggered a LoadCustomerEmails for; emails are
  // fetched lazily when the user opens the 'contact' tab to avoid queuing
  // the request behind the dozen bootstrap calls fired on profile load.
  private readonly loadedEmailsCustomerIds = new Set<number>();

  brands$: Observable<IBrand[]> = of([]);
  tags$: Observable<ITag[]> = of([]);
  categories$: Observable<ICategory[]> = of([]);
  currencies$: Observable<ICurrency[]> = of([]);
  currencies: ICurrency[] = [];
  paymentTerms$: Observable<IPayment_term[]> = of([]);
  paymentTerms: IPayment_term[] = [];
  groupes$: Observable<ICompaniesGroups[]> = of([]);
  filteredGroupes$!: Observable<ICompaniesGroups[]>;
  filteredBrands$!: Observable<IBrand[]>;
  filteredBlacklistBrands$!: Observable<IBrand[]>;
  filteredTags$!: Observable<ITag[]>;
  filteredCategories$!: Observable<ICategory[]>;

  pricebooks: IPriceBookSimple[] = [];
  pricingRules: IPriceBookSimple[] = [];
  promotions: IPriceBookSimple[] = [];
  filteredPromotions: IPriceBookSimple[] = [];
  searchInput = new FormControl('');

  objectCustomFields$: Observable<IObjectCustomFields | null> = this.store.select(state => state.settings.objectCustomFields);
  objectCustomFields: IObjectCustomFields | null = null;
  objectCustomFieldsParams: IObjectCustomFieldsParams = {
    object_type: RelatedObjectsTypes.CUSTOMER,
    record_id: 0,
    visibility: [CustomFieldVisibilityTypes.BACKOFFICE],
    include_definitions: true,
  };

  sortState: {
    catalogs?: { column: string; direction: 'asc' | 'desc' };
    pricingRules?: { column: string; direction: 'asc' | 'desc' };
    promotions?: { column: string; direction: 'asc' | 'desc' };
    favoriteProducts?: { column: ProductFavoriteSortColumn; direction: 'asc' | 'desc' };
  } = {};

  showAiSuggestion = true;
  activityGroups: { label: string; collapsed: boolean; items: any[] }[] = [];
  productFavoritesProducts: ProductFavoriteProductRow[] = [];
  productFavoritesBrands: ProductFavoriteBrandRow[] = [];
  productFavoritesCategories: ProductFavoriteCategoryRow[] = [];
  productLocationId: number | 'all' = 'all';

  preferenceLocationId: number | 'all' = 'all';
  preferenceGroupes: ICompaniesGroups[] = [];
  whitelistedCategories: ICategory[] = [];
  blacklistedCategories: ICategory[] = [];
  whitelistResolvedProducts: IResolvedProduct[] = [];
  blacklistResolvedProducts: IResolvedProduct[] = [];
  whitelistTruncated = false;
  blacklistTruncated = false;
  filteredWhitelistCategories$!: Observable<ICategory[]>;
  filteredBlacklistCategories$!: Observable<ICategory[]>;
  private rawCatalogsPerLocation: globalThis.Map<number, IAvailableCatalogRow[]> = new globalThis.Map();
  private allLocationsCatalogs: AvailableCatalogRow[] = [];

  selectedConversationId: number | null = null;

  // Customer-emails wiring: customerId$ drives both selector chains via switchMap so
  // the Observable refs stay stable for the component lifetime; only the upstream
  // ReplaySubject emits new values, and switchMap unsubscribes the previous inner selector.
  private readonly customerId$ = new ReplaySubject<number>(1);

  readonly aiEmailsErp$: Observable<ICompanyEmail[]> = this.customerId$.pipe(
    switchMap((cid) => this.store.select(selectCustomerEmailsErp(cid))),
  );
  readonly aiEmailsVolta$: Observable<ICompanyEmail[]> = this.customerId$.pipe(
    switchMap((cid) => this.store.select(selectCustomerEmailsVolta(cid))),
  );
  readonly isLoadingEmails$: Observable<boolean> = this.customerId$.pipe(
    switchMap((cid) => this.store.select(selectCustomerEmailsLoadingFor(cid))),
  );
  readonly emailMutationError$: Observable<IStructuredApiError | null> =
    this.store.select(selectCustomerEmailsLastMutationError);

  // Location-emails wiring: company-wide, keyed off customerId$ so the merged
  // list shows every location's emails for this company (not just the store in
  // focus) in one section.
  private readonly loadedLocationEmailsCustomerIds = new Set<number>();

  readonly locationEmailsErp$: Observable<ILocationEmail[]> = this.customerId$.pipe(
    switchMap((cid) => this.store.select(selectLocationEmailsForCompanyErp(cid))),
  );
  readonly locationEmailsVolta$: Observable<ILocationEmail[]> = this.customerId$.pipe(
    switchMap((cid) => this.store.select(selectLocationEmailsForCompanyVolta(cid))),
  );
  readonly isLoadingLocationEmails$: Observable<boolean> = this.customerId$.pipe(
    switchMap((cid) => this.store.select(selectLocationEmailsLoadingForCompany(cid))),
  );
  readonly locationEmailMutationError$: Observable<IStructuredApiError | null> =
    this.store.select(selectLocationEmailsLastMutationError);

  allLocationsForTable: ILocation[] = [];

  formatResponseTime = formatResponseTime;
  getOrderPaymentStatus = getOrderPaymentStatus;
  DATE_TIME_FORMAT = DATE_TIME_FORMAT;
  DATE_FORMAT = DATE_FORMAT;
  currentLanguage: string = this.languageService.getCurrentLanguage();

  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    private fb: FormBuilder,
    private translateService: TranslateService,
    private pricebookService: PricebookService,
    public featureFlagService: FeatureFlagService,
    private languageService: LanguageService,
    private recapHelpers: RecapHelpersService,
  ) {
    this.subscriptions.push(
      this.activatedRoute.queryParams.subscribe(params => {
        const pageNumber = parseInt(params['page'] || '1');
        const pageCount = parseInt(params['count'] || DEFAULT_PAGE_SIZE.toString());
        this.page = { ...this.page, number: pageNumber, size: pageCount };
      }),
    );
  }

  ngOnInit(): void {
    this.store.dispatch(FetchAllCurrencies());
    this.store.dispatch(FetchAllpaymentTerms());

    this.form = this.fb.group({
      brandControl: new FormControl(''),
      blacklistBrandControl: new FormControl(''),
      whitelistCategoryControl: new FormControl(''),
      blacklistCategoryControl: new FormControl(''),
      categoryControl: new FormControl(''),
      tagControl: new FormControl(''),
      groupeControl: new FormControl(''),
    });

    this.locationId = +this.activatedRoute.snapshot.paramMap.get('id')!;
    this.objectCustomFieldsParams.record_id = this.locationId;

    const sub1 = this.actions$.pipe(
      ofType(GetCustomerByIdSuccess),
      tap((action) => {
        this.customer = { ...action.customer };

        // Seed the customerId$ subject so the email selectors are bound to
        // the right customer. The actual LoadCustomerEmails dispatch is
        // deferred until the user opens the 'contact' tab (see
        // setRightPanelTab) -- the Odoo dev server is single-worker and
        // queuing emails behind brands/categories/etc. caused multi-minute
        // TTFB. Lazy-loading on tab activation lets the bootstrap rush
        // clear first.
        if (this.customer.id != null) {
          this.customerId$.next(this.customer.id);
          if (this.rightPanelTab === 'contact') {
            this.ensureEmailsLoaded();
          }
        }

        this.selectedFiles = action.customer?.files || [];
        this.currentLocation = action.customer.locations!.find(l => l.id === this.locationId)
          ?? action.customer.locations![0];
        this.otherLocations = action.customer.locations!.filter(l => l.id !== this.currentLocation.id);

        // The merged email list is company-wide (keyed off customerId$ seeded
        // above), so it needs no per-location re-seed. Load stays deferred to
        // the 'contact' tab.
        if (this.rightPanelTab === 'contact') {
          this.ensureLocationEmailsLoaded();
        }

        this.currentCustomDiscount = this.currentLocation.custom_discount!;
        this.custom_discount.setValue(this.currentCustomDiscount);
        this.blacklist_brands = this.currentLocation.blacklist_brands || [];
        this.favorit_brands = this.currentLocation.favorit_brands || [];
        this.tags = this.currentLocation.tags || [];
        this.categories = this.currentLocation.categories || [];
        this.groupes = this.currentLocation.groups || [];
        this.contacts_information = this.currentLocation.contacts_information || [];
        if (this.currentLocation) {
          this.selectedCatalogs = (this.currentLocation.catalogs || []).map((catalog: ICatalog) => ({
            ...catalog,
            additional_discount: catalog.additional_discount ?? this.currentLocation.custom_discount
          }));

          this.rawCatalogsPerLocation.clear();
          for (const loc of action.customer.locations || []) {
            if (loc.id != null) {
              this.rawCatalogsPerLocation.set(loc.id, loc.available_catalogs || []);
            }
          }
          this.allLocationsCatalogs = this.aggregateAvailableCatalogs(action.customer.locations || []);
          this.availableCatalogs = [...this.allLocationsCatalogs];
          this.applyCatalogFilter();
        }

        const prefLoc = this.preferenceLocationId === 'all'
          ? this.currentLocation
          : (action.customer.locations || []).find(l => l.id === this.preferenceLocationId)
              ?? this.currentLocation;
        this.loadPreferenceDataForLocation(prefLoc);
        this.store.dispatch(SetCurrentElementObject({
          currentElementObject: { id: this.currentLocation.id, is_main: this.currentLocation.is_main }
        }));
        this.isLoaded = true;
        this.buildActivityGroups();
        this.refreshLocationsForMap();
        this.refreshProductFavoritesDisplay();
      })
    ).subscribe();

    const subApprobation = this.actions$.pipe(
      ofType(CustomerApprobationSuccess),
      tap((action) => { this.customer.status = action.customer.status; })
    ).subscribe();

    const subError = this.actions$.pipe(
      ofType(GetCustomerByIdError),
      tap(() => this.navigateBack())
    ).subscribe();

    const subUpdate = this.actions$.pipe(
      ofType(UpdateCustomerSuccess, UpdateCustomerError, AddLocationSuccess,
             UpdateLocationSuccess, UpdateLocationError),
      debounceTime(200),
      tap(() => this.store.dispatch(GetCustomerById({ id: this.locationId })))
    ).subscribe();

    const subDelete = this.actions$.pipe(
      ofType(DeleteCustomerSuccess),
      tap(() => this.navigateBack())
    ).subscribe();

    // After restoring, refresh so the header button flips to Delete.
    const subRestore = this.actions$.pipe(
      ofType(RestoreCustomerSuccess),
      tap(() => this.store.dispatch(GetCustomerById({ id: this.locationId })))
    ).subscribe();

    // Dispatched AFTER all actions$ subscribers are set up to prevent any
    // synchronous GetCustomerByIdSuccess emission from being missed by the
    // hot-stream sub1 — the symptom was emails sometimes never loaded on
    // fast page-loads when the action arrived before the subscriber.
    this.store.dispatch(GetCustomerById({ id: this.locationId }));
    this.store.dispatch(GetObjectCustomFields({ params: this.objectCustomFieldsParams }));

    this.store.dispatch(FetchAllBrands({}));
    this.store.dispatch(FetchAllTags({}));
    this.store.dispatch(FetchAllCategories({}));
    this.store.dispatch(FetchCompaniesGroups({}));
    this.getPricebooksForLocation();

    this.brands$ = this.store.select((state) => [...state.brands.allBrands].sort((a, b) => a.name.localeCompare(b.name)));
    this.tags$ = this.store.select((state) => [...state.tags.allTags].sort((a, b) => a.name.localeCompare(b.name)));
    this.categories$ = this.store.select((state) => [...state.categories.allCategories].sort((a, b) => a.name.localeCompare(b.name)));
    this.groupes$ = this.store.select((state) => [...state.companiesGroups.AllCompaniesGroups].sort((a, b) => a.name.localeCompare(b.name)));
    this.currencies$ = this.store.select((state) => state.customers.allCurrencies);
    this.paymentTerms$ = this.store.select((state) => [...state.customers.allPaymentTerms].sort((a, b) => a.name.localeCompare(b.name)));

    this.filteredBrands$ = combineLatest([
      this.brands$,
      this.form.get('brandControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(map(([brands, input]) =>
      this._filterStrings(brands, input).filter(
        b => !this.favorit_brands.some(x => x.id === b.id)
          && !this.blacklist_brands.some(x => x.id === b.id)
      )
    ));

    this.filteredBlacklistBrands$ = combineLatest([
      this.brands$,
      this.form.get('blacklistBrandControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(map(([brands, input]) =>
      this._filterStrings(brands, input).filter(
        b => !this.favorit_brands.some(x => x.id === b.id)
          && !this.blacklist_brands.some(x => x.id === b.id)
      )
    ));

    this.filteredWhitelistCategories$ = combineLatest([
      this.categories$,
      this.form.get('whitelistCategoryControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(map(([cats, input]) =>
      this._filterStrings(cats, input).filter(
        c => !this.whitelistedCategories.some(x => x.id === c.id)
          && !this.blacklistedCategories.some(x => x.id === c.id)
      )
    ));

    this.filteredBlacklistCategories$ = combineLatest([
      this.categories$,
      this.form.get('blacklistCategoryControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(map(([cats, input]) =>
      this._filterStrings(cats, input).filter(
        c => !this.whitelistedCategories.some(x => x.id === c.id)
          && !this.blacklistedCategories.some(x => x.id === c.id)
      )
    ));

    this.filteredTags$ = combineLatest([
      this.tags$,
      this.form.get('tagControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(map(([tags, input]) => this._filterStrings(tags, input)));

    this.filteredCategories$ = combineLatest([
      this.categories$,
      this.form.get('categoryControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(map(([categories, input]) => this._filterStrings(categories, input)));

    this.filteredGroupes$ = combineLatest([
      this.groupes$,
      this.form.get('groupeControl')!.valueChanges.pipe(startWith(''))
    ]).pipe(map(([groupes, input]) =>
      this._filterStrings(groupes, input).filter(
        g => !this.preferenceGroupes.some(x => x.id === g.id)
      )
    ));

    const subCurrencies = this.currencies$.subscribe((c) => { this.currencies = c; });
    const subPaymentTerms = this.paymentTerms$.subscribe((p) => { this.paymentTerms = p; });
    const subAllGroupes = this.groupes$.subscribe((g) => {
      this.allAvailableGroupes = g;
      if (g.length > 0 && this.preferenceGroupes.length === 0 && this.currentLocation) {
        const loc = this.preferenceLocationId === 'all'
          ? this.currentLocation
          : (this.customer?.locations || []).find(l => l.id === this.preferenceLocationId)
              ?? this.currentLocation;
        if (loc?.group_ids?.length) {
          this.preferenceGroupes = g.filter(gr => (loc.group_ids as number[]).includes(gr.id));
        }
      }
    });

    const subGroupAdd = this.actions$.pipe(
      ofType(AddCompaniesGroupeSuccess),
      tap((action) => this.onGroupeSelected(action.companiesGroup))
    ).subscribe();


    const subSearch = this.searchInput.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      tap((value) => {
        this.filteredPromotions = this.promotions.filter(
          (p: IPriceBookSimple) => p.name.toLowerCase().includes(value?.toLowerCase() || '')
        );
      })
    ).subscribe();

    const subCatalogSearch = this.catalogSearchInput.valueChanges.pipe(
      startWith(''),
      debounceTime(150),
      tap(() => this.applyCatalogFilter())
    ).subscribe();

    const subCustomFields = this.objectCustomFields$.subscribe((cf) => {
      if (cf) this.objectCustomFields = cf;
    });

    this.subscriptions.push(
      sub1, subApprobation, subError, subUpdate, subDelete,
      subCurrencies, subPaymentTerms, subAllGroupes, subGroupAdd,
      subSearch, subCatalogSearch, subCustomFields
    );
  }

  private _filterStrings(strings: any[], value: string): any[] {
    const filterValue = value?.toLowerCase().replace(/\s/g, '');
    return strings.filter((s) => s.name?.toLowerCase().replace(/\s/g, '')?.includes(filterValue!));
  }

  getPricebooksForLocation() {
    if (!this.featureFlagService.loyaltyEnabled) return;
    this.pricebookService.getPricebooksForLocation(this.locationId).subscribe((response) => {
      this.pricebooks = response.data;
      this.pricingRules = this.pricebooks.filter(
        (pb) => this.pricebookService.getPricebookCategoryFromCalculationType(pb.calculation_type!) === 'pricing_rule'
      );
      this.promotions = this.pricebooks.filter(
        (pb) => this.pricebookService.getPricebookCategoryFromCalculationType(pb.calculation_type!) === 'promotion'
      );
      this.filteredPromotions = this.promotions.filter(
        (p) => p.name.toLowerCase().includes(this.searchInput.value?.toLowerCase() || '')
      );
    });
  }

  goTo(form: string) { this.currentForm = form; }
  cancel() { this.currentForm = 'recapCatalog'; }

  onSaveCatalogs(data: SelectedCatalogsData) {
    this.selectedCatalogs = data.selectedCatalogs;
    this.applyCatalogFilter();
    this.currentForm = 'recapCatalog';
    this.currentCustomDiscount = data.customDiscount!;
    this.custom_discount.setValue(data.customDiscount!);
    this.editLocation();
  }

  onGeneralDiscountChange(event: any) {
    const value = +event.target.value;
    if (value > 100) {
      event.target.value = 100;
      this.currentCustomDiscount = 100;
      this.custom_discount.setValue(100);
    } else {
      this.currentCustomDiscount = value;
    }
    this.selectedCatalogs = this.selectedCatalogs.map((c) => ({
      ...c,
      additional_discount: Math.min(value, 100) || undefined
    }));
    this.applyCatalogFilter();
  }

  expired(catalog: { end_date?: string | Date | null } | ICatalog): boolean {
    if (!catalog?.end_date) return false;
    const endDate = new Date(catalog.end_date as string | Date);
    endDate.setHours(23, 59, 59, 999);
    return endDate < new Date();
  }

  sumDiscount(a: number, b: number): number {
    const A = +a / 100;
    const B = +b / 100;
    return Math.ceil(parseFloat(((A + B - (A * B)) * 100).toFixed(1)));
  }

  removeCatalog(catalog: ICatalog) {
    this.selectedCatalogs = this.selectedCatalogs.filter((c) => c.id !== catalog.id);
    this.applyCatalogFilter();
    this.editLocation();
  }

  applyCatalogFilter(): void {
    this.filteredCatalogs = [...this.selectedCatalogs];

    let base: AvailableCatalogRow[];
    if (this.preferenceLocationId === 'all') {
      base = this.allLocationsCatalogs.length ? this.allLocationsCatalogs : [...this.availableCatalogs];
    } else {
      const raw = this.rawCatalogsPerLocation.get(this.preferenceLocationId as number) || [];
      base = raw.map(row => this.normalizeAvailableCatalog(row));
    }

    const term = (this.catalogSearchInput.value || '').toLowerCase().trim();
    this.filteredAvailableCatalogs = term
      ? base.filter(c => (c.catalog_name || '').toLowerCase().includes(term))
      : [...base];
  }

  private normalizeAvailableCatalog(row: IAvailableCatalogRow): AvailableCatalogRow {
    return {
      catalog_id: row.catalog_id,
      image: row.image || null,
      catalog_name: row.catalog_name || '',
      total_units: this.recapHelpers.toNumber(row.total_units),
      total_orders: this.recapHelpers.toNumber(row.total_orders),
      status: row.status || '',
      end_date: row.end_date ?? null,
    };
  }

  /**
   * Aggregates available catalog rows across all locations so the
   * "Select All Locations" view shows summed `total_orders` / `total_units`
   * for catalogs that appear in more than one location.
   */
  private aggregateAvailableCatalogs(locations: ILocation[]): AvailableCatalogRow[] {
    const byCatalog = new globalThis.Map<string, AvailableCatalogRow>();
    let fallbackIndex = 0;

    for (const loc of locations) {
      for (const rawRow of loc.available_catalogs || []) {
        const normalized = this.normalizeAvailableCatalog(rawRow);
        const key = normalized.catalog_id != null
          ? `id:${normalized.catalog_id}`
          : `fallback:${fallbackIndex++}`;

        const existing = byCatalog.get(key);
        if (!existing) {
          byCatalog.set(key, normalized);
          continue;
        }

        existing.total_units += normalized.total_units;
        existing.total_orders += normalized.total_orders;
        existing.image = existing.image || normalized.image;
        existing.catalog_name = existing.catalog_name || normalized.catalog_name;
        existing.status = existing.status || normalized.status;
        existing.end_date = existing.end_date ?? normalized.end_date;
      }
    }

    return Array.from(byCatalog.values());
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = 'assets/icones/default-image-product.svg';
  }

  removeCatalogConfirmation(catalog: ICatalog) {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, { disableClose: true });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) this.removeCatalog(catalog);
    });
  }

  editCatalog(catalog: { id?: number; catalog_id?: number } | ICatalog) {
    const id = (catalog as any)?.catalog_id ?? (catalog as any)?.id;
    if (id == null) return;
    window.open(`/catalogs/edit-catalog/${id}`, '_blank');
  }

  getCatalogDisplayStatus(status: string | null | undefined): string {
    const normalized = (status || '').toString().toLowerCase();
    return normalized === 'approved' ? 'published' : normalized;
  }

  sortFavoriteProducts(column: ProductFavoriteSortColumn): void {
    const current = this.sortState.favoriteProducts;
    const newDirection: 'asc' | 'desc' = current?.column === column && current.direction === 'asc' ? 'desc' : 'asc';
    this.sortState.favoriteProducts = { column, direction: newDirection };

    const dir = newDirection === 'asc' ? 1 : -1;
    this.productFavoritesProducts = [...this.productFavoritesProducts].sort((a, b) => {
      const valA = this.getFavoriteProductSortValue(a, column);
      const valB = this.getFavoriteProductSortValue(b, column);

      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * dir;
      }

      return String(valA).localeCompare(String(valB)) * dir;
    });
  }

  private getFavoriteProductSortValue(product: ProductFavoriteProductRow, column: ProductFavoriteSortColumn): string | number {
    if (column === 'last_ordered') return product.last_ordered?.getTime() || 0;
    return product[column] ?? '';
  }

  sortTable(table: 'catalogs' | 'pricingRules' | 'promotions', column: string) {
    const current = this.sortState[table];
    const newDirection: 'asc' | 'desc' = current?.column === column && current.direction === 'asc' ? 'desc' : 'asc';
    this.sortState[table] = { column, direction: newDirection };

    const dir = newDirection === 'asc' ? 1 : -1;
    const compareFn = (a: any, b: any) => {
      const valA = (a[column] ?? '').toString().toLowerCase();
      const valB = (b[column] ?? '').toString().toLowerCase();
      return valA < valB ? -dir : valA > valB ? dir : 0;
    };

    if (table === 'catalogs') {
      const numericCols: AvailableCatalogSortColumn[] = ['total_units', 'total_orders'];
      const compareAvailable = (a: AvailableCatalogRow, b: AvailableCatalogRow) => {
        if (numericCols.includes(column as AvailableCatalogSortColumn)) {
          const valA = Number((a as any)[column]) || 0;
          const valB = Number((b as any)[column]) || 0;
          return (valA - valB) * dir;
        }
        const valA = ((a as any)[column] ?? '').toString().toLowerCase();
        const valB = ((b as any)[column] ?? '').toString().toLowerCase();
        return valA < valB ? -dir : valA > valB ? dir : 0;
      };
      this.allLocationsCatalogs = [...this.allLocationsCatalogs].sort(compareAvailable);
      this.availableCatalogs = [...this.availableCatalogs].sort(compareAvailable);
      this.applyCatalogFilter();
    } else if (table === 'pricingRules') {
      this.pricingRules = [...this.pricingRules].sort(compareFn);
    } else if (table === 'promotions') {
      this.filteredPromotions = [...this.filteredPromotions].sort(compareFn);
    }
  }


  buildActivityGroups(): void {
    const activities = this.customer?.activities || [];
    if (!activities.length) {
      this.activityGroups = [];
      return;
    }

    const groups: Map<string, any[]> = new Map();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    for (const activity of activities) {
      const d = new Date(activity.date);
      const month = d.getMonth();
      const year = d.getFullYear();

      let label: string;
      if (month === currentMonth && year === currentYear) {
        label = this.translateService.instant('common.this_month');
      } else {
        label = d.toLocaleString(this.currentLanguage || 'en-US', { month: 'long', year: 'numeric' });
      }

      if (!groups.has(label)) {
        groups.set(label, []);
      }
      groups.get(label)!.push(activity);
    }

    this.activityGroups = Array.from(groups.entries()).map(([label, items]) => ({
      label,
      collapsed: false,
      items
    }));
  }

  getActivityIcon(activityType: string): string {
    switch (activityType) {
      case 'consult_catalog': return 'assets/icones/eye.svg';
      case 'request_quotation': return 'assets/icones/cart.svg';
      case 'place_order': return 'assets/icones/activity-bag.svg';
      case 'login': return 'assets/icones/activity-check.svg';
      default: return 'assets/icones/activity-check.svg';
    }
  }

  getActivityBgClass(activityType: string): string {
    return (activityType === 'login' || activityType === 'place_order') ? 'act-card-gray' : '';
  }

  dismissAiSuggestion(): void {
    this.showAiSuggestion = false;
  }

  toggleActivityGroup(index: number): void {
    this.activityGroups[index].collapsed = !this.activityGroups[index].collapsed;
  }

  onProductLocationChange(value: number | 'all'): void {
    this.productLocationId = value;
    this.refreshProductFavoritesDisplay();
  }

  onPreferenceLocationChange(value: number | 'all'): void {
    this.preferenceLocationId = value;
    const loc = value === 'all'
      ? this.currentLocation
      : (this.customer?.locations || []).find(l => l.id === value);
    this.loadPreferenceDataForLocation(loc);
    this.applyCatalogFilter();
  }

  getPreferenceLocationName(id: number | 'all'): string {
    return this.getOrdersLocationName(id);
  }

  /** Supports curated product groups and legacy flat brand arrays. */
  private loadPreferenceDataForLocation(location: ILocation | undefined): void {
    if (!location) {
      this.favorit_brands = [];
      this.blacklist_brands = [];
      this.whitelistedCategories = [];
      this.blacklistedCategories = [];
      this.preferenceGroupes = [];
      this.whitelistResolvedProducts = [];
      this.blacklistResolvedProducts = [];
      this.whitelistTruncated = false;
      this.blacklistTruncated = false;
      return;
    }

    if (location.groups?.length) {
      this.preferenceGroupes = location.groups;
    } else if (location.group_ids?.length) {
      this.preferenceGroupes = this.allAvailableGroupes.filter(g => location.group_ids.includes(g.id));
    } else {
      this.preferenceGroupes = [];
    }

    if (location.whitelisted_products) {
      this.favorit_brands = location.whitelisted_products.selected_brands || [];
      this.whitelistedCategories = location.whitelisted_products.selected_categories || [];
      this.whitelistResolvedProducts = location.whitelisted_products.resolved_products || [];
      this.whitelistTruncated = !!location.whitelisted_products.truncated;
    } else {
      this.favorit_brands = location.favorit_brands || [];
      this.whitelistedCategories = [];
      this.whitelistResolvedProducts = [];
      this.whitelistTruncated = false;
    }

    if (location.blacklisted_products) {
      this.blacklist_brands = location.blacklisted_products.selected_brands || [];
      this.blacklistedCategories = location.blacklisted_products.selected_categories || [];
      this.blacklistResolvedProducts = location.blacklisted_products.resolved_products || [];
      this.blacklistTruncated = !!location.blacklisted_products.truncated;
    } else {
      this.blacklist_brands = location.blacklist_brands || [];
      this.blacklistedCategories = [];
      this.blacklistResolvedProducts = [];
      this.blacklistTruncated = false;
    }

    this.form.get('brandControl')?.setValue('');
    this.form.get('blacklistBrandControl')?.setValue('');
    this.form.get('whitelistCategoryControl')?.setValue('');
    this.form.get('blacklistCategoryControl')?.setValue('');
  }

  onLocationMenuOpened(): void {
    setTimeout(() => {
      const active = document.querySelector(
        '.ord-view-menu .ord-view-menu-list .mat-menu-item.active'
      ) as HTMLElement | null;
      active?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, 0);
  }

  getOrdersLocationName(id: number | 'all'): string {
    if (id === 'all') return this.translateService.instant('companies.orders_tab.all_locations');
    const loc = (this.customer?.locations || []).find((l) => l.id === id);
    return loc?.name || '';
  }

  getProductLocationName(id: number | 'all'): string {
    return this.getOrdersLocationName(id);
  }

  private refreshProductFavoritesDisplay(): void {
    const locations = this.getSelectedProductFavoriteLocations();
    this.productFavoritesProducts = this.mergeFavoriteProducts(locations);
    this.productFavoritesBrands = this.mergeFavoriteBrands(locations);
    this.productFavoritesCategories = this.mergeFavoriteCategories(locations);
  }

  private getSelectedProductFavoriteLocations(): ILocation[] {
    const locations = this.customer?.locations || [];
    if (this.productLocationId === 'all') return locations;
    const location = locations.find((loc) => loc.id === this.productLocationId);
    return location ? [location] : [];
  }

  private mergeFavoriteProducts(locations: ILocation[]): ProductFavoriteProductRow[] {
    const byProduct = new globalThis.Map<string, ProductFavoriteProductRow>();

    for (const location of locations) {
      for (const product of location.favorite_products || []) {
        const normalized = this.recapHelpers.normalizeFavoriteProduct(product);
        const key = normalized.product_code || normalized.product_name;
        if (!key) continue;

        const existing = byProduct.get(key);
        if (!existing) {
          byProduct.set(key, normalized);
          continue;
        }

        existing.total_units += normalized.total_units;
        existing.total_orders += normalized.total_orders;
        existing.total_revenue += normalized.total_revenue;
        existing.image = existing.image || normalized.image;
        existing.product_name = existing.product_name || normalized.product_name;
        existing.product_code = existing.product_code || normalized.product_code;
        existing.last_ordered = this.recapHelpers.getNewestDate(existing.last_ordered, normalized.last_ordered);
      }
    }

    return Array.from(byProduct.values());
  }

  private mergeFavoriteBrands(locations: ILocation[]): ProductFavoriteBrandRow[] {
    const byBrand = new globalThis.Map<string, ProductFavoriteBrandRow>();

    for (const location of locations) {
      for (const brand of location.favorite_brands || []) {
        const normalized = this.recapHelpers.normalizeFavoriteBrand(brand);
        if (!normalized.brand_name) continue;

        const existing = byBrand.get(normalized.brand_name);
        if (!existing) {
          byBrand.set(normalized.brand_name, normalized);
          continue;
        }

        existing.total_units += normalized.total_units;
        existing.total_orders += normalized.total_orders;
        existing.total_revenue += normalized.total_revenue;
      }
    }

    return Array.from(byBrand.values());
  }

  private mergeFavoriteCategories(locations: ILocation[]): ProductFavoriteCategoryRow[] {
    const byCategory = new globalThis.Map<string, ProductFavoriteCategoryRow>();

    for (const location of locations) {
      for (const category of location.favorite_categories || []) {
        const normalized = this.recapHelpers.normalizeFavoriteCategory(category);
        if (!normalized.category_name) continue;

        const existing = byCategory.get(normalized.category_name);
        if (!existing) {
          byCategory.set(normalized.category_name, normalized);
          continue;
        }

        existing.total_units += normalized.total_units;
        existing.total_orders += normalized.total_orders;
        existing.total_revenue += normalized.total_revenue;
      }
    }

    return Array.from(byCategory.values());
  }

  addContacts(contact?: IMainContact) {
    // WB-7370: entry points into this dialog are already hidden when the flag is off; guard
    // here too so a stale binding or future regression can't reach it.
    if (!this.featureFlagService.traditionalContactsEnabled) return;
    const allLocs = ([this.currentLocation, ...this.otherLocations] as (ILocation | null | undefined)[])
      .filter((l): l is ILocation => !!l);

    let owningLocationId = this.locationId;
    if (contact?.id != null) {
      const owningLoc = allLocs.find(loc =>
        (loc.contacts_information ?? []).some(c => c.id === contact.id)
      );
      if (owningLoc?.id != null) owningLocationId = owningLoc.id;
    }

    const dialogRef = this.dialog.open(AddContactsComponent, {
      data: {
        contacts_information: this.contacts_information,
        currentContact: contact,
        locationId: owningLocationId,
        availableLocations: allLocs,
        showLocationSelector: !contact, // only show selector when creating
      },
      disableClose: true,
      panelClass: 'ac-dialog-panel',
    });
    dialogRef.afterClosed().subscribe((result: { newContact?: LocationContact; updatedContact?: LocationContact; deleteContactId?: number; locationId?: number }) => {
      if (!result) return;
      const targetId = result.locationId ?? owningLocationId;
      const asContact = (c: LocationContact) => c as unknown as IMainContact;

      // Delete is triggered from inside the edit dialog; reuse the confirm + delete flow.
      // If the user backs out of the confirmation, reopen the edit dialog they came from.
      if (result.deleteContactId != null && targetId != null) {
        this.deleteContact({ contactId: result.deleteContactId, locationId: targetId }, () => this.addContacts(contact));
        return;
      }

      if (result.newContact) {
        if (targetId === this.currentLocation?.id) {
          this.contacts_information = [...this.contacts_information, asContact(result.newContact)];
        } else {
          this.otherLocations = this.otherLocations.map(loc =>
            loc.id === targetId
              ? { ...loc, contacts_information: [...(loc.contacts_information ?? []), asContact(result.newContact!)] }
              : loc
          );
        }
      } else if (result.updatedContact) {
        if (targetId === this.currentLocation?.id) {
          this.contacts_information = this.contacts_information.map(c =>
            c.id === result.updatedContact!.id ? asContact(result.updatedContact!) : c
          );
        } else {
          this.otherLocations = this.otherLocations.map(loc =>
            loc.id === targetId
              ? {
                  ...loc,
                  contacts_information: (loc.contacts_information ?? []).map(c =>
                    c.id === result.updatedContact!.id ? asContact(result.updatedContact!) : c
                  ),
                }
              : loc
          );
        }
      }
    });
  }

  deleteContact({ contactId, locationId }: { contactId: number; locationId: number }, onCancel?: () => void) {
    // WB-7370: entry points into this flow are already hidden when the flag is off; guard here
    // too so a stale binding or future regression can't reach it.
    if (!this.featureFlagService.traditionalContactsEnabled) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, { disableClose: true });
    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        onCancel?.();
        return;
      }
      if (locationId === this.currentLocation?.id) {
        this.contacts_information = this.contacts_information.filter(c => c.id !== contactId);
      } else {
        this.otherLocations = this.otherLocations.map(loc =>
          loc.id === locationId
            ? { ...loc, contacts_information: (loc.contacts_information ?? []).filter(c => c.id !== contactId) }
            : loc
        );
      }
      this.store.dispatch(DeleteLocationContact({ locationId, contactId }));
    });
  }

  editFiles(files: IFile[] = []) {
    const dialogRef = this.dialog.open(EditFilesComponent, {
      data: { files },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe((result: IFile[]) => {
      if (result) {
        this.selectedFiles = [...result];
        this.editLocation();
      }
    });
  }

  download(data: string, fileName: string, content_type: string) {
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: content_type });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  preview(data: string, content_type: string) {
    const byteCharacters = atob(data);
    const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: content_type });
    this.dialog.open(FilesDialogPreviewerComponent, { data: [blob] });
  }

  onBlackListBrandSelected(brand: IBrand) {
    if (!brand) return;
    if (!this.blacklist_brands.find(b => b.id === brand.id)
      && !this.favorit_brands.find(b => b.id === brand.id)) {
      this.blacklist_brands = [...this.blacklist_brands, brand];
      this.saveProductPreferences();
    }
    this.form.get('blacklistBrandControl')?.setValue('');
  }
  cancelBlackListBrand(id: number) {
    this.blacklist_brands = this.blacklist_brands.filter(b => b.id !== id);
    this.form.get('blacklistBrandControl')?.setValue('');
    this.saveProductPreferences();
  }

  onFavoritBrandSelected(brand: IBrand) {
    if (!brand) return;
    if (!this.favorit_brands.find(b => b.id === brand.id)
      && !this.blacklist_brands.find(b => b.id === brand.id)) {
      this.favorit_brands = [...this.favorit_brands, brand];
      this.saveProductPreferences();
    }
    this.form.get('brandControl')?.setValue('');
  }
  cancelFavoritBrand(id: number) {
    this.favorit_brands = this.favorit_brands.filter(b => b.id !== id);
    this.form.get('brandControl')?.setValue('');
    this.saveProductPreferences();
  }

  private saveGroups(): void {
    const groupIds = this.preferenceGroupes.map(g => g.id);
    if (this.preferenceLocationId === 'all') {
      for (const loc of (this.customer?.locations || [])) {
        if (loc.id != null) {
          this.store.dispatch(UpdateLocation({ id: loc.id, payload: { group_ids: groupIds } }));
        }
      }
    } else {
      this.store.dispatch(UpdateLocation({
        id: this.preferenceLocationId as number,
        payload: { group_ids: groupIds },
      }));
    }
  }

  /**
   * Narrow PATCH for whitelist/blacklist chip edits.
   *
   * Sending the full `editLocation()` payload here would re-serialize unrelated
   * fields (notably `payment_terms`) from `currentLocation`, which can leak a
   * string value into a numeric backend field and trigger a 400. Restricting
   * the payload to product-preference IDs keeps types intact and avoids
   * unintended overwrites of unrelated location data.
   */
  private saveProductPreferences(): void {
    const payload: LocationUpdatePayload = {
      whitelisted_brand_ids:     this.favorit_brands.map(b => b.id),
      whitelisted_category_ids:  this.whitelistedCategories.map(c => c.id),
      blacklisted_brand_ids:     this.blacklist_brands.map(b => b.id),
      blacklisted_category_ids:  this.blacklistedCategories.map(c => c.id),
    };

    if (this.preferenceLocationId === 'all') {
      for (const loc of (this.customer?.locations || [])) {
        if (loc.id != null) {
          this.store.dispatch(UpdateLocation({ id: loc.id, payload }));
        }
      }
    } else {
      this.store.dispatch(UpdateLocation({
        id: this.preferenceLocationId as number,
        payload,
      }));
    }
  }

  onGroupeSelected(groupe: ICompaniesGroups) {
    if (!groupe) return;
    if (!this.preferenceGroupes.find(g => g.id === groupe.id)) {
      this.preferenceGroupes = [...this.preferenceGroupes, groupe];
      this.saveGroups();
    }
    this.form.get('groupeControl')?.setValue('');
  }

  cancelGroupe(id: number) {
    this.preferenceGroupes = this.preferenceGroupes.filter(g => g.id !== id);
    this.form.get('groupeControl')?.setValue('');
    this.saveGroups();
  }

  onWhitelistCategorySelected(category: ICategory): void {
    if (!category) return;
    if (!this.whitelistedCategories.find(c => c.id === category.id)
      && !this.blacklistedCategories.find(c => c.id === category.id)) {
      this.whitelistedCategories = [...this.whitelistedCategories, category];
      this.saveProductPreferences();
    }
    this.form.get('whitelistCategoryControl')?.setValue('');
  }

  cancelWhitelistCategory(id: number): void {
    this.whitelistedCategories = this.whitelistedCategories.filter(c => c.id !== id);
    this.form.get('whitelistCategoryControl')?.setValue('');
    this.saveProductPreferences();
  }

  onBlacklistCategorySelected(category: ICategory): void {
    if (!category) return;
    if (!this.blacklistedCategories.find(c => c.id === category.id)
      && !this.whitelistedCategories.find(c => c.id === category.id)) {
      this.blacklistedCategories = [...this.blacklistedCategories, category];
      this.saveProductPreferences();
    }
    this.form.get('blacklistCategoryControl')?.setValue('');
  }

  cancelBlacklistCategory(id: number): void {
    this.blacklistedCategories = this.blacklistedCategories.filter(c => c.id !== id);
    this.form.get('blacklistCategoryControl')?.setValue('');
    this.saveProductPreferences();
  }

  onChipInputBlur(type: 'brand' | 'blacklistBrand' | 'groupe' | 'whitelistCategory' | 'blacklistCategory') {
    setTimeout(() => this.form.get(type + 'Control')?.setValue(''), 300);
  }

  onTagSelected(tag: ITag) {
    if (tag) {
      this.form.get('tagControl')?.setValue('');
      if (!this.tags.find(t => t.id === tag.id)) {
        this.tags = [...this.tags, tag];
      }
    }
  }
  cancelTag(id: number) { this.tags = this.tags.filter(t => t.id !== id); }

  onCategorySelected(category: ICategory) {
    if (category) {
      this.form.get('categoryControl')?.setValue('');
      if (!this.categories.find(c => c.id === category.id)) {
        this.categories = [...this.categories, category];
      }
    }
  }
  cancelCategory(id: number) { this.categories = this.categories.filter(c => c.id !== id); }

  addNewElement(name: string, type: string) {
    const dialogRef = this.dialog.open(AddElementsComponent, {
      data: { name, type },
      disableClose: true,
    });
    dialogRef.afterClosed().subscribe(() => {
      this.autocompleteTriggers.forEach(trigger => trigger.closePanel());
    });
  }

  paymentTerm(term_id: number): string {
    const term = this.paymentTerms.find(t => t.id === term_id);
    return term?.id ? this.translateService.instant(term.name) : '';
  }

  currency(currency_id: number): ICurrency | undefined {
    return this.currencies.find(c => c.id === +currency_id);
  }

  getPricebookCalculationTypeDisplayText(type: PriceBookTypeValue): string {
    return this.pricebookService.getPricebookCalculationTypeDisplayText(type);
  }

  deleteLocation() {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, { disableClose: true });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) this.store.dispatch(DeleteLocation({ id: this.locationId }));
    });
  }

  /** Soft-delete (archive) the whole company from the detail header. */
  deleteCompany() {
    const id = this.customer?.id;
    if (id == null) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.archive'),
        text: this.translateService.instant('companies.archive_confirm_message'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) this.store.dispatch(DeleteCustomer({ id }));
    });
  }

  /** Restore (reactivate) the archived company from the detail header. */
  restoreCompany() {
    const id = this.customer?.id;
    if (id == null) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.restore'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) this.store.dispatch(RestoreCustomer({ id }));
    });
  }

  confirmDeleteLocationRow(location: ILocation) {
    const id = location?.id;
    if (id == null) return;
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, { disableClose: true });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) return;
      this.store.dispatch(DeleteLocation({ id }));

      if (this.currentLocation?.id === id) {
        this.actions$.pipe(ofType(DeleteLocationSuccess), take(1)).subscribe(() => {
          this.store.dispatch(GetCustomerById({ id: this.locationId }));
        });
        return;
      }

      this.actions$.pipe(ofType(DeleteLocationSuccess), take(1)).subscribe(() => {
        this.removeLocationLocally(id);
      });
    });
  }

  private removeLocationLocally(id: number): void {
    this.otherLocations = (this.otherLocations || []).filter(l => l.id !== id);
    if (this.customer?.locations) {
      this.customer = {
        ...this.customer,
        locations: this.customer.locations.filter(l => l.id !== id),
      };
    }
    this.refreshLocationsForMap();
  }

  companyApprobation(status: string) {
    let data: any = {
      confirm_text: this.translateService.instant('companies.approve'),
      title: this.translateService.instant('companies.company_approbation'),
      text: this.translateService.instant('companies.approbation_ask'),
    };
    if (status === 'rejected') {
      data.confirm_text = this.translateService.instant('companies.reject');
      data.title = this.translateService.instant('companies.company_rejection');
      data.text = this.translateService.instant('companies.reject_ask');
    }
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, { data, disableClose: true });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) this.store.dispatch(CustomerApprobation({ id: this.customer.id, status }));
    });
  }

  editCustomFields() {
    this.dialog.open(CustomerCustomFieldsComponent, {
      data: { objectCustomFields: this.objectCustomFields }
    }).afterClosed().subscribe((result: any) => {
      if (result) {
        this.store.dispatch(UpdateObjectCustomFields({
          params: { ...this.objectCustomFieldsParams },
          values: result,
        }));
      }
    });
  }

  addLocations() {
    const dialogData: AddLocationDialogData = {
      currencies: this.currencies,
      paymentTerms: this.paymentTerms,
      contacts: this.contacts_information,
      isMainLocationExists: this.allLocationsHaveMain(),
    };
    const dialogRef = this.dialog.open(AddLocationsComponent, {
      data: dialogData,
      disableClose: true,
      panelClass: 'al-dialog-panel',
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        const payload = this.buildCreateLocationPayload(result, this.customer.id);
        this.store.dispatch(AddLocation({ payload }));
      }
    });
  }

  /** Edit dialog; demotes the old main when promoting a new one. */
  editLocationDialog(location: ILocation) {
    const allLocations = ([this.currentLocation, ...this.otherLocations] as (ILocation | null | undefined)[])
      .filter((l): l is ILocation => !!l);
    const dialogData: AddLocationDialogData = {
      currentLocation: location,
      currencies: this.currencies,
      paymentTerms: this.paymentTerms,
      contacts: location.contacts_information ?? [],
      isMainLocationExists: this.allLocationsHaveMain(),
      allLocations,
    };
    const dialogRef = this.dialog.open(AddLocationsComponent, {
      data: dialogData,
      disableClose: true,
      panelClass: 'al-dialog-panel',
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        if (result.is_main && !location.is_main) {
          const oldMain = allLocations.find(l => l.is_main && l.id !== location.id);
          if (oldMain) {
            this.store.dispatch(UpdateLocation({ id: oldMain.id!, payload: { is_main: false } }));
            if (oldMain.id === this.currentLocation?.id) {
              this.currentLocation = { ...this.currentLocation, is_main: false };
            } else {
              this.otherLocations = this.otherLocations.map(l =>
                l.id === oldMain.id ? { ...l, is_main: false } : l
              );
            }
          }
        }

        const isCurrentLocation = location.id === this.currentLocation?.id;
        if (isCurrentLocation) {
          this.currentLocation = {
            ...this.currentLocation,
            name: result.name,
            tax_id: result.tax_id,
            company_id: result.erp_id,
            apartment: result.apartment,
            billing_apartment: result.billing_apartment,
            is_bill_same: result.is_bill_same,
            currency: this.currencies.find(c => c.id === +result.currency),
            payment_terms: this.paymentTerms.find(p => p.id === +result.payment_terms),
            is_main: result.is_main,
            country: result.country,
            state: result.state,
            city: result.city,
            zip: result.zip,
            billing_country: result.billing_country,
            billing_state: result.billing_state,
            billing_city: result.billing_city,
            billing_zip: result.billing_zip,
            lang: result.lang,
          };
          this.contacts_information = result.contacts_information?.length
            ? result.contacts_information
            : this.contacts_information;
          this.editLocation();
        } else {
          const editPayload: LocationUpdatePayload = {};

          if (result.name)              editPayload.name              = (result.name as string).trim();
          if (result.is_main != null)   editPayload.is_main           = result.is_main;
          if (result.apartment)         editPayload.apartment         = result.apartment;
          if (result.billing_apartment) editPayload.billing_apartment = result.billing_apartment;
          if (result.payment_terms)     editPayload.payment_terms     = +result.payment_terms;
          if (result.tax_id)            editPayload.tax_id            = (result.tax_id as string).trim().substring(0, 64);
          if (result.erp_id)            editPayload.erp_id            = (result.erp_id as string).trim().substring(0, 64);

          if (result.city)                editPayload.city               = result.city;
          if (result.zip)                 editPayload.zip                = result.zip;
          if (result.country?.id)         editPayload.country            = { id: result.country.id, name: result.country.name };
          if (result.billing_city)        editPayload.billing_city       = result.billing_city;
          if (result.billing_zip)         editPayload.billing_zip        = result.billing_zip;
          if (result.billing_country?.id) editPayload.billing_country    = { id: result.billing_country.id, name: result.billing_country.name };
          if (result.lang)                editPayload.lang               = result.lang;
          if (result.is_bill_same != null) editPayload.is_bill_same      = result.is_bill_same;

          const currencyId = +result.currency;
          if (currencyId) {
            const curr = this.currencies.find(c => c.id === currencyId);
            if (curr) editPayload.currency = { id: curr.id, name: curr.name };
          }
          if (result.tax != null)             editPayload.tax             = result.tax;
          if (result.custom_discount != null) editPayload.custom_discount = +result.custom_discount;

          editPayload.group_ids                = location.groups?.map(g => g.id) || [];
          editPayload.tag_ids                  = location.tags?.map(t => t.id) || [];
          editPayload.whitelisted_brand_ids    = location.whitelisted_products?.selected_brands?.map(b => b.id) ?? location.favorit_brand_ids ?? [];
          editPayload.whitelisted_category_ids = location.whitelisted_products?.selected_categories?.map(c => c.id) ?? location.whitelisted_category_ids ?? [];
          editPayload.blacklisted_brand_ids    = location.blacklisted_products?.selected_brands?.map(b => b.id) ?? location.blacklist_brand_ids ?? [];
          editPayload.blacklisted_category_ids = location.blacklisted_products?.selected_categories?.map(c => c.id) ?? location.blacklisted_category_ids ?? [];
          editPayload.contacts_information = result.contacts_information ?? location.contacts_information ?? [];

          this.store.dispatch(UpdateLocation({ id: location.id!, payload: editPayload }));
        }
      }
    });
  }

  private allLocationsHaveMain(): boolean {
    const all = [this.currentLocation, ...this.otherLocations];
    return all.some(l => l?.is_main);
  }

  private buildCreateLocationPayload(result: any, companyId: number): CreateLocationPayload {
    const payload: Partial<CreateLocationPayload> = {
      name: (result.name as string).trim(),
      id_company: companyId,
    };

    if (result.is_main !== undefined && result.is_main !== null) {
      payload.is_main = result.is_main;
    }

    const apt = result.apartment?.trim();
    if (apt) payload.apartment = apt;

    const bApt = result.billing_apartment?.trim();
    if (bApt) payload.billing_apartment = bApt;

    const ptId = +result.payment_terms;
    if (ptId) payload.payment_terms = ptId;

    const erpId = result.erp_id?.trim().substring(0, 64);
    if (erpId) payload.erp_id = erpId;

    const taxId = result.tax_id?.trim().substring(0, 64);
    if (taxId) payload.tax_id = taxId;

    if (Array.isArray(result.contacts_information) && result.contacts_information.length) {
      payload.contacts_information = result.contacts_information;
    }

    return payload as CreateLocationPayload;
  }

  editLocations(location: ILocation) {
    this.router.navigate(['edit-customers', location.id], { queryParams: { action: 'edit' } });
    this.store.dispatch(SetCurrentElementId({ currentElementId: location.id }));
  }

  openLocation(location: ILocation) {
    this.router.navigate(['/companies', location.id], { queryParamsHandling: 'preserve' });
  }


  get activeResolvedProducts(): IResolvedProduct[] {
    return this.activeResolvedType === 'whitelist'
      ? this.whitelistResolvedProducts
      : this.blacklistResolvedProducts;
  }

  get activeFilteredProducts(): IResolvedProduct[] {
    const q = this.rpSearchQuery.toLowerCase().trim();
    if (!q) return this.activeResolvedProducts;
    return this.activeResolvedProducts.filter(p =>
      (p.name || '').toLowerCase().includes(q) ||
      (p.code || '').toLowerCase().includes(q)
    );
  }

  get activeResolvedTruncated(): boolean {
    return this.activeResolvedType === 'whitelist'
      ? this.whitelistTruncated
      : this.blacklistTruncated;
  }

  get activeResolvedTitle(): string {
    return this.activeResolvedType === 'whitelist'
      ? 'Whitelisted Products'
      : 'Blacklisted Products';
  }

  get activeResolvedSelectionBrands(): IBrand[] {
    return this.activeResolvedType === 'whitelist' ? this.favorit_brands : this.blacklist_brands;
  }

  get activeResolvedSelectionCategories(): ICategory[] {
    return this.activeResolvedType === 'whitelist' ? this.whitelistedCategories : this.blacklistedCategories;
  }

  openResolvedProductsDialog(type: 'whitelist' | 'blacklist'): void {
    this.activeResolvedType = type;
    this.rpSearchQuery = '';
    this.dialog.open(this.resolvedProductsDialogTpl, {
      width: '520px',
      maxHeight: '85vh',
      panelClass: 'rp-dialog-panel',
      autoFocus: false,
    });
  }

  editLocation() {
    const loc = this.currentLocation;
    const payload: LocationUpdatePayload = {};

    if (loc.name)            payload.name      = loc.name.trim();
    if (loc.is_main != null) payload.is_main   = loc.is_main;
    if (loc.apartment)       payload.apartment = loc.apartment;
    if (loc.city)            payload.city      = loc.city;
    if (loc.zip)             payload.zip       = loc.zip;
    if (loc.country?.id)     payload.country   = { id: loc.country.id, name: loc.country.name };
    if (loc.lang)            payload.lang      = loc.lang;

    if (loc.is_bill_same != null)         payload.is_bill_same         = loc.is_bill_same;
    if (loc.is_bill_same_as_main != null) payload.is_bill_same_as_main = loc.is_bill_same_as_main;
    if (loc.billing_apartment)            payload.billing_apartment    = loc.billing_apartment;
    if (loc.billing_city)                 payload.billing_city         = loc.billing_city;
    if (loc.billing_zip)                  payload.billing_zip          = loc.billing_zip;
    if (loc.billing_country?.id)          payload.billing_country      = { id: loc.billing_country.id, name: loc.billing_country.name };

    if (loc.tax_id)     payload.tax_id = loc.tax_id.trim().substring(0, 64);
    if (loc.company_id) payload.erp_id = loc.company_id.trim().substring(0, 64);

    if (loc.currency?.id)              payload.currency        = { id: loc.currency.id, name: loc.currency.name };
    if (loc.tax != null)               payload.tax             = loc.tax;
    if (loc.payment_terms?.id)         payload.payment_terms   = loc.payment_terms.id;
    if (this.currentCustomDiscount != null) payload.custom_discount = this.currentCustomDiscount;

    payload.tag_ids                   = this.tags.map(t => t.id);
    payload.whitelisted_brand_ids     = this.favorit_brands.map(b => b.id);
    payload.whitelisted_category_ids  = this.whitelistedCategories.map(c => c.id);
    payload.blacklisted_brand_ids     = this.blacklist_brands.map(b => b.id);
    payload.blacklisted_category_ids  = this.blacklistedCategories.map(c => c.id);
    payload.contacts_information      = this.contacts_information;
    payload.catalogs             = this.selectedCatalogs.map(c => ({
      id: c.id,
      ...(c.additional_discount != null ? { additional_discount: c.additional_discount } : {}),
    }));

    this.store.dispatch(UpdateLocation({ id: loc.id!, payload }));
  }

  navigateBack() {
    this.router.navigate(['/companies'], { queryParamsHandling: 'preserve' });
  }

  ngAfterViewInit(): void {}

  private refreshLocationsForMap(): void {
    const list: ILocation[] = [];
    if (this.currentLocation) list.push(this.currentLocation);
    if (this.otherLocations?.length) list.push(...this.otherLocations);
    this.allLocationsForTable = list;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.customerId$.complete();
  }

  // ── Right-panel tab + lazy email loader ─────────────────────────────────────

  setRightPanelTab(tab: string): void {
    this.rightPanelTab = tab;
    if (tab === 'contact') {
      this.ensureEmailsLoaded();
      this.ensureLocationEmailsLoaded();
    }
  }

  private ensureEmailsLoaded(): void {
    const cid = this.customer?.id;
    if (cid == null || this.loadedEmailsCustomerIds.has(cid)) {
      return;
    }
    this.loadedEmailsCustomerIds.add(cid);
    this.store.dispatch(LoadCustomerEmails({ customerId: cid }));
  }

  private ensureLocationEmailsLoaded(): void {
    const cid = this.customer?.id;
    if (cid == null || this.loadedLocationEmailsCustomerIds.has(cid)) {
      return;
    }
    this.loadedLocationEmailsCustomerIds.add(cid);
    this.store.dispatch(LoadCompanyLocationEmails({ customerId: cid }));
  }

  // ── Merged extra-emails handlers (kind-routed: company vs location) ──────────

  onAddEmailClicked(payload: { email: string; locationId: number | null }): void {
    if (payload.locationId != null) {
      if (this.customer?.id != null) {
        this.store.dispatch(CreateLocationEmail({
          payload: { location_id: payload.locationId, email: payload.email },
          customerId: this.customer.id,
        }));
      }
      return;
    }
    if (this.customer?.id != null) {
      this.store.dispatch(CreateCustomerEmail({
        payload: { customer_id: this.customer.id, email: payload.email },
      }));
    }
  }

  onEditEmailClicked(payload: { id: number; email: string; kind: 'company' | 'location'; locationId: number | null }): void {
    if (payload.kind === 'location' && payload.locationId != null) {
      if (this.customer?.id != null) {
        this.store.dispatch(UpdateLocationEmail({
          id: payload.id,
          locationId: payload.locationId,
          customerId: this.customer.id,
          payload: { email: payload.email },
        }));
      }
      return;
    }
    if (this.customer?.id != null) {
      this.store.dispatch(UpdateCustomerEmail({
        id: payload.id,
        customerId: this.customer.id,
        payload: { email: payload.email },
      }));
    }
  }

  onDeleteEmailClicked(payload: { id: number; kind: 'company' | 'location'; locationId: number | null }): void {
    if (payload.kind === 'location' && payload.locationId != null) {
      if (this.customer?.id != null) {
        this.store.dispatch(DeleteLocationEmail({
          id: payload.id,
          locationId: payload.locationId,
          customerId: this.customer.id,
        }));
      }
      return;
    }
    if (this.customer?.id != null) {
      this.store.dispatch(DeleteCustomerEmail({
        id: payload.id,
        customerId: this.customer.id,
      }));
    }
  }
}
